import * as React from "react";

import { LabeledControl } from "@replo/design-system/blocks/labeled-control/LabeledControl";
import { Button } from "@replo/design-system/components/button/Button";
import { Input } from "@replo/design-system/components/input/Input";
import { Modal } from "@replo/design-system/components/modal/Modal";

interface MinimalTipTapLinkEditorModalProps {
  linkUrl: string;
  linkText: string;
  onSave: (url: string, text: string) => void;
  onCancel?: () => void;
}

export const MinimalTipTapLinkEditorModal: React.FC<
  MinimalTipTapLinkEditorModalProps & {
    open: boolean;
    onOpenChange: (open: boolean) => void;
  }
> = ({
  linkUrl: initialLinkUrl,
  linkText: initialLinkText,
  onSave,
  onCancel,
  open,
  onOpenChange,
}) => {
  const [linkUrl, setLinkUrl] = React.useState(initialLinkUrl);
  const [linkText, setLinkText] = React.useState(initialLinkText);

  const handleSave = React.useCallback(() => {
    onSave(linkUrl, linkText);
    onOpenChange(false);
  }, [onSave, linkUrl, linkText, onOpenChange]);

  const handleCancel = React.useCallback(() => {
    onCancel?.();
    onOpenChange(false);
  }, [onCancel, onOpenChange]);

  const handleModalKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter") {
        handleSave();
      } else if (e.key === "Escape") {
        handleCancel();
      }
    },
    [handleSave, handleCancel],
  );

  return (
    <Modal
      open={open}
      onOpenChange={(newOpen) => {
        if (!newOpen) {
          handleCancel();
        }
      }}
      title="Add link"
      size="base"
      footer={
        <div className="flex gap-2 justify-end">
          <Button variant="secondary" size="base" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="primary" size="base" onClick={handleSave}>
            Save
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <LabeledControl label="Text">
          <Input
            type="text"
            size="base"
            value={linkText}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              return setLinkText(e.target.value);
            }}
            placeholder="Add link text"
            autoFocus={!linkText}
            onKeyDown={handleModalKeyDown}
          />
        </LabeledControl>
        <LabeledControl label="Link">
          <Input
            type="url"
            size="base"
            value={linkUrl}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              return setLinkUrl(e.target.value);
            }}
            placeholder="Link URL"
            autoFocus={!linkText}
            onKeyDown={handleModalKeyDown}
          />
        </LabeledControl>
      </div>
    </Modal>
  );
};
